import type {
  ChannelId,
  NotificationType,
  TenantRole,
  TenantNotificationSettings,
} from "@praesto/shared-types";

/** Sparse (role x type) default routing matrix — see TenantNotificationSettings["rules"]. */
export type Rules = NonNullable<TenantNotificationSettings["rules"]>;

/** The channels configured for a (role, type) cell, or `undefined` if unset (falls back to default). */
export function toChannels(
  rules: Rules | undefined,
  role: TenantRole,
  type: NotificationType,
): ReadonlyArray<ChannelId> | undefined {
  return rules?.[role]?.[type];
}

/**
 * Classifies a (role, type) cell:
 *  - "default": no rule configured — falls back to the system default channels.
 *  - "off": an explicit empty rule — no channels for this role/type.
 *  - "custom": an explicit non-empty rule.
 */
export function cellState(
  rules: Rules | undefined,
  role: TenantRole,
  type: NotificationType,
): "default" | "custom" | "off" {
  const cell = toChannels(rules, role, type);
  if (cell === undefined) return "default";
  return cell.length === 0 ? "off" : "custom";
}

/** Returns a new `Rules` object with the (role, type) cell set to `channels`
 *  (`undefined` removes the cell entirely, resetting it to "default"). */
export function setCell(
  rules: Rules | undefined,
  role: TenantRole,
  type: NotificationType,
  channels: ReadonlyArray<ChannelId> | undefined,
): Rules {
  const next: Rules = { ...(rules ?? {}) };
  const forRole = { ...(next[role] ?? {}) };
  if (channels === undefined) delete forRole[type];
  else forRole[type] = channels;
  if (Object.keys(forRole).length === 0) delete next[role];
  else next[role] = forRole;
  return next;
}
